/**
 * A held work order, in words (FM Technician A11; decisions D16, D18).
 *
 * The server holds a job for one of the D16 reasons — or because the permit
 * covering it expired (D18) — and refuses to complete it until the FM resumes
 * the SLA (or a new permit is issued). The job page reads `held_at`,
 * `hold_reason` and `hold_note` and says so: a pill, a banner, and the reason
 * beside a Complete that is off.
 */

/** The reason codes, as a person reads them. */
export const HOLD_REASON_LABEL: Record<string, string> = {
  spares_approval: 'Waiting for spares approval',
  vendor: 'Waiting for vendor',
  no_access: 'No access to the area',
  requester_unavailable: 'Requester not available',
  permit_expired: 'Permit expired',
  // FM Technician review R2-1: one job runs at a time. These two the
  // technician resumes themselves once their other job is finished or held.
  sent_back: 'Sent back while another job was running',
  reassigned: 'Moved to you while another job was running',
  // FM Technician B38: the technician started an emergency job; theirs to resume.
  emergency: 'Held while you ran an emergency',
  // FM Technician B14 (guide walk): can't do on a running job stops the run; the FM decides.
  cant_do: "Reported as can't do",
};

/** Holds the technician lifts with Resume (not the FM). */
export const SELF_RESUMABLE_HOLDS = new Set(['sent_back', 'reassigned', 'emergency']);

/** "Permit expired"; an unknown code still reads ("Some reason"), never blank. */
export function holdReasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'On hold';
  const known = HOLD_REASON_LABEL[reason];
  if (known) return known;
  const words = reason.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'On hold';
}

/**
 * Why Complete is off on a held job. The technician waits for the FM; the
 * FM (who holds maintenance:configure, the SLA resume) lifts it themselves.
 *
 * `slaPaused`: a hold that never paused the SLA (can't do, the busy holds)
 * is lifted with the page's "Lift hold" button, not "Resume clock" — there
 * is no clock to resume, so "Resume the SLA" would send the FM looking for a
 * button that is not there (can't-do follow-up).
 */
export function holdCompleteWhy(reason: string | null | undefined, canResume: boolean,
                                slaPaused = true): string {
  const label = holdReasonLabel(reason);
  // Review R2-2: "press Resume" is the technician's own way out — an FM
  // (canResume) lifts any hold with the SLA resume, so is told that.
  if (!canResume && reason && SELF_RESUMABLE_HOLDS.has(reason)) {
    return `On hold: ${label}. Finish or hold your other job, then press Resume`;
  }
  if (canResume) {
    return slaPaused ? `On hold: ${label}. Resume the SLA to lift the hold`
      : `On hold: ${label}. Press Lift hold to lift it`;
  }
  return `On hold: ${label} — your FM resumes it`;
}

/** A permit the job names: an id, or an id with its number. */
export type HeldPermitRef = string | { id: string; number?: string | null };

export function permitRef(p: HeldPermitRef): { id: string; number: string | null } {
  return typeof p === 'string' ? { id: p, number: null } : { id: p.id, number: p.number ?? null };
}
